import React, { createContext, useContext, useEffect, useState } from "react"
import { useAuth as useClerkAuth, useUser } from "@clerk/clerk-react"
import { HAS_CLERK_PUBLISHABLE_KEY } from "../lib/config"
import { isAdminEmail, checkIsOwner, ensureUserDocument } from "../services/appwriteAuth"

const AuthContext = createContext()

function ClerkAuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isOwner, setIsOwner] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const { isLoaded, isSignedIn, signOut } = useClerkAuth()
  const { user: clerkUser } = useUser()

  useEffect(() => {
    const syncAuthState = async () => {
      if (!isLoaded) {
        return
      }

      if (!isSignedIn || !clerkUser) {
        setUser(null)
        setIsOwner(false)
        setIsAdmin(false)
        setLoading(false)
        return
      }

      try {
        const email = clerkUser.primaryEmailAddress?.emailAddress || ""
        const baseUser = {
          $id: clerkUser.id,
          id: clerkUser.id,
          email,
          name: clerkUser.fullName || clerkUser.firstName || email.split("@")[0] || "User",
          prefs: { avatar: clerkUser.imageUrl || "" },
        }

        const userDoc = await ensureUserDocument(baseUser)
        const userObj = {
          uid: clerkUser.id,
          email,
          displayName: userDoc?.displayName || baseUser.name,
          photoURL: userDoc?.photoURL || clerkUser.imageUrl || "",
          clerkUserId: clerkUser.id,
          ...userDoc,
        }

        setUser(userObj)
        setIsAdmin(isAdminEmail(email))
        setIsOwner(await checkIsOwner(email))
      } catch (error) {
        console.error("Auth sync error:", error)
        setUser({
          uid: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress || "",
          displayName: clerkUser.fullName || clerkUser.firstName || "User",
          photoURL: clerkUser.imageUrl || "",
          clerkUserId: clerkUser.id,
        })
        setIsAdmin(isAdminEmail(clerkUser.primaryEmailAddress?.emailAddress || ""))
        setIsOwner(false)
      } finally {
        setLoading(false)
      }
    }

    syncAuthState()
  }, [isLoaded, isSignedIn, clerkUser])

  const handleSignOut = async () => {
    await signOut()
  }

  const value = {
    user,
    isAdmin,
    isOwner,
    loading,
    refreshAuth: () => {},
    signOut: handleSignOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function FallbackAuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setUser(null)
    setLoading(false)
  }, [])

  const handleSignOut = async () => {
    setUser(null)
  }

  const value = {
    user,
    isAdmin: false,
    isOwner: false,
    loading,
    refreshAuth: () => {},
    signOut: handleSignOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function AuthProvider({ children }) {
  if (!HAS_CLERK_PUBLISHABLE_KEY) {
    return <FallbackAuthProvider>{children}</FallbackAuthProvider>
  }

  return <ClerkAuthProvider>{children}</ClerkAuthProvider>
}

export function useAuth() {
  return useContext(AuthContext)
}

